import { useQuery } from "@apollo/client/react";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import { BackwardBack } from "@pmag/icons";
// eslint-disable-next-line no-restricted-imports
import { InlineError, Pagination, Switch, Typography } from "@pmag/ui-components";
import { type GQLFulltextSearchModelsQuery, type GQLFulltextSearchModelsQueryVariables, type GQLModelFilter } from "@src/graphql.generated";
import { useAttributeConfig } from "@src/hooks/useAttributeConfig";
import { useGlobalState } from "@src/hooks/useGlobalState";
import { useRestart } from "@src/main/useRestart";
import { fulltextSearchQuery, SEARCH_PAGE_LIMIT } from "@src/pages/landingPage/components/searchContainer/Search.gql";
import { VehicleCard } from "@src/pages/searchResult/components/VehicleCard";
import * as React from "react";
import { FormattedMessage } from "react-intl";

import { NavigationButton, Root, TopContainer } from "./SearchResultPage.styles";

export const dataTestId = {
    container: "sparePartsFinder.searchResult.container",
    buttonRestart: "sparePartsFinder.searchResult.button.restart",
    inlineError: "sparePartsFinder.searchResult.inlineError",
};

export const SearchResultPage: React.FunctionComponent = () => {
    const { restart } = useRestart();
    const { searchTerm, supportedBrandIds } = useGlobalState();

    const theme = useTheme();
    const defaultRegion = useAttributeConfig().region;
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

    const [currentPage, setCurrentPage] = React.useState<number>(1);
    const [defaultRegionModelsOnly, setDefaultRegionModelsOnly] = React.useState<boolean>(true);

    let filter: GQLModelFilter = defaultRegionModelsOnly
        ? { specification: defaultRegion, isWpArticle: { equal: false }, hasChildren: false, hasVisibleDescendants: true }
        : { isWpArticle: { equal: false }, hasChildren: false, hasVisibleDescendants: true };
    if (supportedBrandIds.length > 0) {
        filter = { ...filter, brand: { isAnyOf: supportedBrandIds } };
    }
    const { data, error, refetch } = useQuery<GQLFulltextSearchModelsQuery, GQLFulltextSearchModelsQueryVariables>(fulltextSearchQuery, {
        variables: { query: searchTerm, filter: filter, offset: (currentPage - 1) * SEARCH_PAGE_LIMIT },
    });

    if (error) return <InlineError onRetry={refetch} data-testid={dataTestId.inlineError} />;

    const totalNumberOfPages = data ? Math.ceil(data.models.totalCount / SEARCH_PAGE_LIMIT) : 0;

    return (
        <Root data-testid={dataTestId.container}>
            <TopContainer showBorderBottom={true}>
                {!isSmallScreen && (
                    <Typography variant="heading-07">
                        <FormattedMessage id="sparePartsFinder.searchResult.title" defaultMessage="Your result" />
                    </Typography>
                )}

                <NavigationButton variant="text" startIcon={<BackwardBack size="small" />} data-testid={dataTestId.buttonRestart} onClick={restart}>
                    <Typography variant="body-short-01">
                        <FormattedMessage id="sparePartsFinder.common.button.restart" defaultMessage="Restart" />
                    </Typography>
                </NavigationButton>
            </TopContainer>

            {isSmallScreen && (
                <Box mb={2}>
                    <Typography variant="body-short-02">
                        <FormattedMessage
                            id="sparePartsFinder.searchResult.searchedFor"
                            defaultMessage="You have searched for: {searchTerm}"
                            values={{ searchTerm: searchTerm }}
                        />
                    </Typography>
                </Box>
            )}

            <TopContainer>
                {!isSmallScreen && (
                    <Typography variant="body-short-02">
                        <FormattedMessage
                            id="sparePartsFinder.searchResult.searchedFor"
                            defaultMessage="You have searched for: {searchTerm}"
                            values={{ searchTerm: searchTerm }}
                        />
                    </Typography>
                )}

                <Typography variant="heading-08">
                    <FormattedMessage
                        id="sparePartsFinder.searchResult.results"
                        defaultMessage="{count} Results"
                        values={{ count: data?.models.totalCount ?? 0 }}
                    />
                </Typography>

                {defaultRegion !== "" && (
                    <Switch
                        checked={defaultRegionModelsOnly}
                        onChange={(_, value) => setDefaultRegionModelsOnly(value)}
                        label={
                            <FormattedMessage
                                id="sparePartsFinder.common.defaultRegionModelsOnly"
                                defaultMessage="{region} models only"
                                values={{ region: defaultRegion }}
                            />
                        }
                    />
                )}
            </TopContainer>

            {data?.models.nodes.map((model) => (
                <VehicleCard key={model.id} model={model} showBrandLogo />
            ))}

            <Pagination fullWidth={true} count={totalNumberOfPages} page={currentPage} onChange={(e, newPage) => setCurrentPage(newPage)} />
        </Root>
    );
};
